// src/components/publishing/draft-preview-tabs.tsx
import React, { useState } from "react";
import {
  FileCode2, Globe, Linkedin, Save, AlertTriangle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  publishingApi, Destination, destinationLabel,
  Draft, PerDestinationContent, PolicyWarning, HashnodeContent,
  WordPressContent, LinkedInContent,
} from "@/app/services/publishing/publishing-api";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import { HashnodeEditor, WordPressEditor, LinkedInEditor } from "./destination-field-editors";
import { ClaimList } from "../content/content-draft-panel";
import type { Claim, ClaimSummary } from "@/api/content";

/** Confirming a claim as a fact is admin-only (the server refuses anyone else). */
function viewerIsAdmin() {
  return localStorage.getItem("is_admin") === "true" || sessionStorage.getItem("is_admin") === "true";
}

/** #132: the claim check rides on the draft's source_ref, per destination. */
function claimCheck(draft: Draft, d: Destination): { claims: Claim[]; summary: ClaimSummary } | null {
  return (draft.source_ref?.claim_check || {})[d] || null;
}

interface DraftPreviewTabsProps {
  draft: Draft;
  onUpdated: (updated: Draft) => void;
}

export function DraftPreviewTabs({ draft, onUpdated }: DraftPreviewTabsProps) {
  const [active, setActive] = useState<Destination>(draft.destinations[0]);
  const [edited, setEdited] = useState<PerDestinationContent>(draft.per_destination_content || {});
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState<PolicyWarning[]>(draft.policy_warnings || []);

  const dirty = JSON.stringify(edited) !== JSON.stringify(draft.per_destination_content);

  const activeWarnings = warnings.filter(w => w.destination === active);
  const activeCheck = claimCheck(draft, active);
  const evidence: string | undefined = draft.source_ref?.evidence;
  const [resolving, setResolving] = useState(false);

  const refresh = (fresh: Draft) => {
    setEdited(fresh.per_destination_content || {});
    onUpdated(fresh);
  };
  const resolve = async (c: Claim, resolution: "confirm_fact" | "remove") => {
    setResolving(true);
    try {
      refresh(await publishingApi.resolveClaim(draft.id, { destination: active, sentence_index: c.sentence_index, resolution }));
      toast.success(resolution === "remove" ? "Sentence removed" : "Recorded as a fact in the brand record");
    } catch (e: any) {
      toast.error(e.message || "Could not resolve the claim");
    } finally {
      setResolving(false);
    }
  };
  const recheck = async () => {
    setResolving(true);
    try {
      refresh(await publishingApi.recheckClaims(draft.id));
      toast.success("Claims checked again");
    } catch (e: any) {
      toast.error(e.message || "Could not check the claims");
    } finally {
      setResolving(false);
    }
  };

  const handleChange = (
    destination: Destination,
    patch: Partial<HashnodeContent & WordPressContent & LinkedInContent>,
  ) => {
    setEdited(prev => ({
      ...prev,
      [destination]: { ...(prev[destination] || {}), ...patch },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await publishingApi.updateDraft(draft.id, edited);
      setWarnings(res.policy_warnings || []);
      const fresh = await publishingApi.getDraft(draft.id);
      setEdited(fresh.per_destination_content || {});
      onUpdated(fresh);
      toast.success("Draft saved");
    } catch (e: any) {
      toast.error(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Tabs */}
      <div style={{
        display: "flex", gap: 4, borderBottom: "1px solid var(--border)",
        overflowX: "auto",
      }}>
        {draft.destinations.map(d => {
          const hasHard = warnings.some(w => w.destination === d && w.severity === "hard")
            || (claimCheck(draft, d)?.summary?.blocking || 0) > 0;
          return (
            <DestTab
              key={d}
              destination={d}
              active={active === d}
              hasViolation={hasHard}
              onClick={() => setActive(d)}
            />
          );
        })}
      </div>

      {activeWarnings.length > 0 && (
        <PolicyWarnings warnings={activeWarnings} />
      )}

      {evidence && evidence !== "none" && (
        <div style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", fontSize: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>Claim check</strong>
            <span style={{ color: evidence === "action" ? "var(--muted-foreground)" : "#B45309" }}>
              {evidence === "action" ? "Written from an action's brief"
                : evidence === "facts_only" ? "Facts, voice and market only (no completed read)"
                : "No action, no evidence"}
            </span>
            {activeCheck && (
              <span style={{ color: "var(--muted-foreground)" }}>
                · {activeCheck.summary.supported} supported, {activeCheck.summary.unverifiable} unverifiable,{" "}
                {activeCheck.summary.contradicted} contradicted
                {activeCheck.summary.unchecked ? `, ${activeCheck.summary.unchecked} not yet checked` : ""}
              </span>
            )}
            <button onClick={recheck} disabled={resolving || dirty} title={dirty ? "Save your edits first" : undefined}
              style={{ marginLeft: "auto", fontSize: 11, padding: "3px 9px", borderRadius: 6,
                       border: "1px solid var(--border)", background: "var(--background)", cursor: "pointer" }}>
              Check claims again
            </button>
          </div>
          {activeCheck
            ? <ClaimList claims={activeCheck.claims} busy={resolving || dirty} onResolve={resolve}
                         canConfirm={viewerIsAdmin()} />
            : <span style={{ color: "var(--muted-foreground)" }}>No text to check for this destination.</span>}
          {(activeCheck?.summary?.blocking || 0) > 0 && (
            <p style={{ color: "#B45309", marginTop: 6 }}>
              This destination cannot be published until every unverifiable or contradicted claim is edited,
              confirmed as a fact by an admin, or removed, and every claim has been checked by the model
              (Check claims again).
            </p>
          )}
        </div>
      )}

      {/* Per-destination editor */}
      <div>
        {active === "hashnode" && (
          <HashnodeEditor
            content={(edited.hashnode || {}) as HashnodeContent}
            onChange={patch => handleChange("hashnode", patch)}
          />
        )}
        {active === "wordpress" && (
          <WordPressEditor
            content={(edited.wordpress || {}) as WordPressContent}
            onChange={patch => handleChange("wordpress", patch)}
          />
        )}
        {active === "linkedin" && (
          <LinkedInEditor
            content={(edited.linkedin || {}) as LinkedInContent}
            onChange={patch => handleChange("linkedin", patch)}
          />
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", borderRadius: 8,
            border: "1px solid var(--border)",
            backgroundColor: dirty && !saving ? "var(--background)" : "var(--muted)",
            color: "var(--foreground)",
            cursor: dirty && !saving ? "pointer" : "not-allowed",
            fontSize: 12, fontWeight: 600,
          }}
        >
          {saving
            ? <Loader2 style={{ width: 12, height: 12 }} className="animate-spin" />
            : <Save style={{ width: 12, height: 12 }} />
          }
          {saving ? "Saving…" : dirty ? "Save edits" : "Saved"}
        </button>
      </div>
    </div>
  );
}

// ─── Tab button ──────────────────────────────────────────────────────────────

function DestTab({
  destination, active, hasViolation, onClick,
}: {
  destination: Destination;
  active: boolean;
  hasViolation: boolean;
  onClick: () => void;
}) {
  const meta = {
    hashnode:  { Icon: FileCode2, color: "#2962FF" },
    wordpress: { Icon: Globe,     color: "#21759B" },
    linkedin:  { Icon: Linkedin,  color: "#0A66C2" },
  }[destination];

  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "8px 14px",
        border: "none", backgroundColor: "transparent", cursor: "pointer",
        borderBottom: active ? "2px solid #0D9488" : "2px solid transparent",
        color: active ? "#0D9488" : "var(--muted-foreground)",
        fontSize: 13, fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      <meta.Icon style={{ width: 13, height: 13, color: active ? "#0D9488" : meta.color }} />
      {destinationLabel(destination)}
      {hasViolation && (
        <AlertTriangle style={{ width: 12, height: 12, color: "#EF4444" }} />
      )}
    </button>
  );
}

// ─── Policy warnings ─────────────────────────────────────────────────────────

function PolicyWarnings({ warnings }: { warnings: PolicyWarning[] }) {
  const hard = warnings.filter(w => w.severity === "hard");
  const soft = warnings.filter(w => w.severity === "soft");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {hard.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Must fix before publishing</AlertTitle>
          <AlertDescription>
            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
              {hard.map((w, i) => <li key={i}>{w.message}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      {soft.length > 0 && (
        <div style={{
          padding: "8px 12px", borderRadius: 8,
          backgroundColor: "rgba(245,158,11,0.06)",
          border: "1px solid rgba(245,158,11,0.2)",
          fontSize: 12, color: "#B45309",
        }}>
          <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", display: "block", marginBottom: 3 }}>
            Notes
          </strong>
          <ul style={{ margin: "0 0 0 16px", padding: 0 }}>
            {soft.map((w, i) => <li key={i}>{w.message}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}